import React from 'react';
import { Trash2, Link as LinkIcon } from 'lucide-react';
import clsx from 'clsx';
import { InterfaceType } from '../../types/fta.types';

interface EdgePropertiesPanelProps {
    selectedEdge: any;
    selectedEdgeId: string | null;
    updateSelectedEdge: (type: InterfaceType) => void;
    setEdges: any;
    setSelectedEdgeId: (id: string | null) => void;
}

export const EdgePropertiesPanel: React.FC<EdgePropertiesPanelProps> = ({
    selectedEdge,
    selectedEdgeId,
    updateSelectedEdge,
    setEdges,
    setSelectedEdgeId
}) => {
    if (!selectedEdge) return null;

    return (
        <>
            <h3 className="font-semibold text-lg text-slate-800 dark:text-white mb-4 flex items-center gap-2">
                <LinkIcon size={18} className="text-slate-400" />
                Connection Type
            </h3>

            <div className="space-y-4">
                <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
                    Specify the type of interface or failure propagation path according to AIAG-VDA standards.
                </p>

                <div className="space-y-2">
                    <button
                        onClick={() => updateSelectedEdge(InterfaceType.DEFAULT)}
                        className={clsx(
                            "w-full flex items-center gap-3 p-3 rounded-lg border text-sm transition-all",
                            selectedEdge.data?.interfaceType === InterfaceType.DEFAULT
                                ? "bg-indigo-50 dark:bg-indigo-900/30 border-indigo-200 dark:border-indigo-800 ring-1 ring-indigo-500/20"
                                : "bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600"
                        )}
                    >
                        <div className="w-8 h-0.5 bg-slate-500"></div>
                        <span className="font-medium text-slate-700 dark:text-slate-200">Default (Logical)</span>
                    </button>

                    <button
                        onClick={() => updateSelectedEdge(InterfaceType.PHYSICAL)}
                        className={clsx(
                            "w-full flex items-center gap-3 p-3 rounded-lg border text-sm transition-all",
                            selectedEdge.data?.interfaceType === InterfaceType.PHYSICAL
                                ? "bg-slate-100 dark:bg-slate-600 border-slate-300 dark:border-slate-500 ring-1 ring-slate-400"
                                : "bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600"
                        )}
                    >
                        <div className="w-8 h-1 bg-slate-600"></div>
                        <span className="font-medium text-slate-700 dark:text-slate-200">Physical (PH)</span>
                    </button>

                    <button
                        onClick={() => updateSelectedEdge(InterfaceType.MATERIAL)}
                        className={clsx(
                            "w-full flex items-center gap-3 p-3 rounded-lg border text-sm transition-all",
                            selectedEdge.data?.interfaceType === InterfaceType.MATERIAL
                                ? "bg-sky-50 dark:bg-sky-900/30 border-sky-200 dark:border-sky-800 ring-1 ring-sky-500/20"
                                : "bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600"
                        )}
                    >
                        <div className="w-8 h-0.5 bg-sky-500 border-t-2 border-dashed border-sky-500"></div>
                        <span className="font-medium text-slate-700 dark:text-slate-200">Material (ME)</span>
                    </button>

                    <button
                        onClick={() => updateSelectedEdge(InterfaceType.ENERGY)}
                        className={clsx(
                            "w-full flex items-center gap-3 p-3 rounded-lg border text-sm transition-all",
                            selectedEdge.data?.interfaceType === InterfaceType.ENERGY
                                ? "bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800 ring-1 ring-amber-500/20"
                                : "bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600"
                        )}
                    >
                        <div className="w-8 h-1 bg-amber-500"></div>
                        <span className="font-medium text-slate-700 dark:text-slate-200">Energy (ET)</span>
                    </button>

                    <button
                        onClick={() => updateSelectedEdge(InterfaceType.DATA)}
                        className={clsx(
                            "w-full flex items-center gap-3 p-3 rounded-lg border text-sm transition-all",
                            selectedEdge.data?.interfaceType === InterfaceType.DATA
                                ? "bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800 ring-1 ring-green-500/20"
                                : "bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600"
                        )}
                    >
                        <div className="w-8 h-0.5 border-t-2 border-dotted border-green-500"></div>
                        <span className="font-medium text-slate-700 dark:text-slate-200">Data (DX)</span>
                    </button>

                    <button
                        onClick={() => updateSelectedEdge(InterfaceType.HUMAN)}
                        className={clsx(
                            "w-full flex items-center gap-3 p-3 rounded-lg border text-sm transition-all",
                            selectedEdge.data?.interfaceType === InterfaceType.HUMAN
                                ? "bg-purple-50 dark:bg-purple-900/30 border-purple-200 dark:border-purple-800 ring-1 ring-purple-500/20"
                                : "bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600"
                        )}
                    >
                        <div className="w-8 h-0.5 border-t-2 border-purple-500" style={{ borderStyle: 'dashed' }}></div>
                        <span className="font-medium text-slate-700 dark:text-slate-200">Human (HM)</span>
                    </button>
                </div>

                <div className="pt-4 border-t border-slate-100 dark:border-slate-700">
                    <button
                        onClick={() => {
                            setEdges((eds: any) => eds.filter((e: any) => e.id !== selectedEdgeId));
                            setSelectedEdgeId(null);
                        }}
                        className="w-full flex items-center justify-center gap-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 border border-transparent hover:border-red-200 dark:hover:border-red-800 rounded px-3 py-2 text-sm transition-colors"
                    >
                        <Trash2 size={16} />
                        Delete Connection
                    </button>
                </div>
            </div>
        </>
    );
};
